import { createSignal, For, onCleanup, onMount, Show } from "solid-js";

interface RelayRow {
  relayId: string;
  publicUrl: string;
  region: string | null;
  kind: string | null;
  status: string | null;
  loadFactor: number | null;
  bandwidthMbps?: number | null;
  bandwidthMbpbs?: number | null;
  lastHeartbeat: string | null;
  parentPublicRelayId: string | null;
}

export function SuperAdminRelaysPage() {
  const [relays, setRelays] = createSignal<RelayRow[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);

  function relayBandwidthMbps(r: RelayRow) {
    return r.bandwidthMbps ?? r.bandwidthMbpbs ?? null;
  }

  function loadRelays() {
    return fetch("/api/super-admin/relays", { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then((data) => {
        setRelays(data.relays || []);
        setError(null);
        setLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
  }

  onMount(() => {
    // Chargement initial via REST
    loadRelays();

    // Filet de sécurité : refresh REST périodique. Les annonces mesh n'arrivent
    // que toutes les 45 s et le SSE peut se couper ; ce poll garantit que la vue
    // se remplit et reflète l'état (statut/heartbeat) même sans flux temps réel.
    const poll = setInterval(loadRelays, 20_000);

    // SSE — abonnement temps réel aux annonces relay
    const es = new EventSource("/api/super-admin/relays/stream", { withCredentials: true });

    es.addEventListener("relay", (ev) => {
      try {
        const update = JSON.parse(ev.data) as RelayRow;
        setRelays((prev) => {
          const idx = prev.findIndex((r) => r.relayId === update.relayId);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...next[idx], ...update };
            return next;
          }
          return [...prev, update];
        });
      } catch {
        /* ignore malformed */
      }
    });

    es.addEventListener("error", () => {
      // La connexion SSE va se reconnecter automatiquement.
      // On ne montre pas d'erreur ici pour éviter le flicker.
    });

    onCleanup(() => {
      clearInterval(poll);
      es.close();
    });
  });

  return (
    <div class="plugins-console" style={{ overflow: "auto" }}>
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Relays
          <span class="plugins-console-sub">mesh relays — temps réel SSE</span>
        </span>
        <button class="btn btn--xs" type="button" onClick={() => void loadRelays()}>
          ↺ Rafraîchir
        </button>
      </div>

      <Show when={error()}>
        <div
          style={{
            background: "rgba(220,38,38,0.12)",
            border: "1px solid var(--danger, #dc2626)",
            color: "var(--danger, #f87171)",
            padding: "6px 10px",
            "border-radius": "4px",
            "font-size": "12px",
            "margin-bottom": "10px",
          }}
        >
          {error()}
        </div>
      </Show>

      <div class="rmm-table-wrap">
        <table class="rmm-table">
          <thead>
            <tr>
              <th>Relay</th>
              <th>URL</th>
              <th>Région</th>
              <th>Kind</th>
              <th>Statut</th>
              <th>Load</th>
              <th>Bande passante</th>
              <th>Heartbeat</th>
            </tr>
          </thead>
          <tbody>
            <Show
              when={!loading()}
              fallback={
                <tr>
                  <td colSpan={8}>Chargement…</td>
                </tr>
              }
            >
              <For each={relays()}>
                {(r) => (
                  <tr>
                    <td style={{ "font-family": "var(--font-mono)", "font-size": "11px", "font-weight": "500" }}>
                      {r.relayId}
                    </td>
                    <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>{r.publicUrl}</td>
                    <td style={{ "font-size": "11px" }}>{r.region || "—"}</td>
                    <td>
                      <span
                        style={{
                          "font-size": "10px",
                          padding: "1px 6px",
                          "border-radius": "4px",
                          background: r.kind === "edge" ? "rgba(96,165,250,0.12)" : "rgba(148,163,184,0.12)",
                          color: r.kind === "edge" ? "var(--info, #60a5fa)" : "var(--text-4)",
                        }}
                      >
                        {r.kind || "public"}
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          "font-size": "10px",
                          "text-transform": "uppercase",
                          padding: "1px 6px",
                          "border-radius": "4px",
                          background:
                            r.status === "active"
                              ? "rgba(74,222,128,0.12)"
                              : r.status === "degraded"
                                ? "rgba(251,191,36,0.12)"
                                : "rgba(220,38,38,0.12)",
                          color:
                            r.status === "active"
                              ? "var(--ok, #4ade80)"
                              : r.status === "degraded"
                                ? "var(--warn, #fbbf24)"
                                : "var(--danger, #f87171)",
                        }}
                      >
                        {r.status || "—"}
                      </span>
                    </td>
                    <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>
                      {r.loadFactor != null ? r.loadFactor.toFixed(2) : "—"}
                    </td>
                    <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>
                      {relayBandwidthMbps(r) != null ? `${relayBandwidthMbps(r)!.toFixed(1)} Mbps` : "—"}
                    </td>
                    <td style={{ "font-size": "11px", color: "var(--text-4)" }}>
                      {r.lastHeartbeat ? new Date(r.lastHeartbeat).toLocaleString() : "—"}
                    </td>
                  </tr>
                )}
              </For>
              <Show when={!error() && relays().length === 0}>
                <tr>
                  <td colSpan={8} style={{ color: "var(--text-4)", "text-align": "center" }}>
                    Aucun relay.
                  </td>
                </tr>
              </Show>
            </Show>
          </tbody>
        </table>
      </div>
    </div>
  );
}
